
import './playerInfoSearch.scss';
import React, { useState, useEffect, useContext } from 'react';
import { Card, Radio, Input, Button, Space, Typography, Row, Col } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { internalApi } from '../../common/axios';
import { defaultPlayerData } from '../../shared/contexts/playerContext';
import { defaultUserSearchParams } from "../../shared/utils/defaultData";
import toast from "react-hot-toast";
import { buildUrl } from "../../shared/utils/net";
import { useLocation } from 'react-router-dom';
import { handleKeyDown } from "../../shared/utils/basic";
import { DarkModeContext } from "../../shared/contexts/darkModeContext";

const { Text, Title } = Typography;

const PlayerInfoSearch = ({ db, setPlayerData, setDefaultState = () => {}, searchChecker = () => {} }) => {
    const { darkMode } = useContext(DarkModeContext);
    const location = useLocation();
    const [searchParams, setSearchParams] = useState(defaultUserSearchParams);
    const [isFromUrl, setIsFromUrl] = useState(false);
    const [loading, setLoading] = useState(false);

    // URL 쿼리 파라미터에서 값 가져오기
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const type = params.get('type');
        const value = params.get('value');

        if (type && value) {
            setSearchParams(prevState => ({
                ...prevState,
                type,
                value
            }));
            setIsFromUrl(true);
        }
    }, [location.search]);

    // 자동 검색 실행 (URL에서 변경된 경우만)
    useEffect(() => {
        if (isFromUrl) {
            handleSearch();
            setIsFromUrl(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFromUrl]);

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleTypeChange = (e) => {
        setSearchParams(prevState => ({
            ...prevState,
            type: e.target.value
        }));
    };

    const handleSearch = async () => {
        if (!searchParams.value.trim()) {
            toast.error('검색어를 입력해주세요.');
            return;
        }

        setLoading(true);

        try {
            const url = buildUrl('player', {
                type: searchParams.type,
                value: searchParams.value,
                target: db
            });

            const res = await internalApi.get(url);

            if (res.data.player === null || res.status !== 200) {
                toast.error(res.data.message);
                setPlayerData(defaultPlayerData);
                return;
            }

            setPlayerData(res.data.player);
            setDefaultState(res.data.player.userState);
            toast.success('플레이어 정보를 조회했습니다.');
            searchChecker(true);
        } catch (err) {
            console.error(err);
            toast.error('검색 중 오류가 발생했습니다.');
            setPlayerData(defaultPlayerData);
        } finally {
            setLoading(false);
        }
    };

    const searchOptions = [
        { label: 'PID', value: 'pid' },
        { label: '닉네임', value: 'name' },
        { label: 'UDID', value: 'udid' },
        { label: 'UserKey', value: 'userKey' }
    ];

    return (
        <Card
            className={`player-search-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            styles={{
                header: {
                    borderBottom: `1px solid var(--border-color)`,
                    background: `var(--header-background)`,
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px'
                },
                body: {
                    padding: '20px',
                    background: `var(--card-background)`
                }
            }}
            style={{
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                background: `var(--card-background)`,
                boxShadow: 'var(--card-shadow)'
            }}
        >
            <div className="search-header">
                <Space align="center" size="middle">
                    <div className="search-icon">
                        <SearchOutlined />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: 0, color: 'var(--text-primary)' }}>
                            플레이어 검색
                        </Title>
                        <Text
                            type="secondary"
                            style={{
                                fontSize: '12px',
                                color: 'var(--text-secondary)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                maxWidth: '100%'
                            }}
                        >
                            검색 조건을 선택하고 값을 입력하세요
                        </Text>
                    </div>
                </Space>
            </div>

            <div className="search-content">
                <div className="search-type-section">
                    <Text strong className="section-label">검색 조건</Text>
                    <Radio.Group
                        value={searchParams.type}
                        onChange={handleTypeChange}
                        className="search-radio-group compact-radio"
                        size="small"
                    >
                        <Row gutter={[16, 8]}>
                            {searchOptions.map(option => (
                                <Col key={option.value} xs={12} sm={6}>
                                    <Radio value={option.value} className="search-radio">
                                        {option.label}
                                    </Radio>
                                </Col>
                            ))}
                        </Row>
                    </Radio.Group>
                </div>

                <div className="search-input-section">
                    <Text strong className="section-label">검색어</Text>
                    <div className="search-input-container">
                        <Input
                            name="value"
                            value={searchParams.value}
                            onChange={handleSearchChange}
                            onKeyDown={e => handleKeyDown(e, handleSearch)}
                            placeholder={`${searchOptions.find(opt => opt.value === searchParams.type)?.label || '검색어'}를 입력하세요`}
                            size="large"
                            prefix={<UserOutlined style={{ color: 'var(--text-tertiary)' }} />}
                            className="search-input"
                            allowClear
                        />
                        <Button
                            type="primary"
                            size="large"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            className="search-button"
                        >
                            검색
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default PlayerInfoSearch;